import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Rocket, 
  MessageSquare, 
  Sparkles, 
  Send,
  ArrowRight,
  Eye,
  CheckCircle2
} from 'lucide-react';
import DemoFunnelViewer from './DemoFunnelViewer';

/**
 * Welcome screen for first-time users (no funnels yet)
 * Shows a friendly onboarding with 3 simple steps explaining the tool
 */
const WelcomeScreen = ({ userName }) => {
  const navigate = useNavigate();
  const [showDemoViewer, setShowDemoViewer] = useState(false);

  const firstName = userName?.split(' ')[0] || 'ahí';

  const steps = [
    {
      number: 1,
      icon: MessageSquare,
      title: 'Cuéntanos tu producto',
      description: 'Responde unas preguntas sobre tu negocio y a quién te diriges',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      number: 2,
      icon: Sparkles,
      title: 'La IA crea tu funnel completo',
      description: 'Landings, emails, WhatsApp, copies y creatividades en minutos',
      color: 'from-violet-500 to-purple-500'
    },
    {
      number: 3,
      icon: Send,
      title: 'Publica en tus herramientas',
      description: 'Conecta con Meta Ads, Google Ads, tu CRM y herramientas de email',
      color: 'from-emerald-500 to-teal-500'
    }
  ];

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center px-4 py-8">
      {/* Greeting */}
      <div className="text-center mb-8 animate-fade-in">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 rounded-full text-sm font-medium mb-6">
          <Rocket className="w-4 h-4" />
          <span>Tu primera vez aquí</span>
        </div>
        
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
          ¡Hola, {firstName}! 
          <span className="inline-block ml-2 animate-wave">👋</span>
        </h1>
        
        <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
          Vamos a crear tu primer funnel de ventas completo en menos de 5 minutos
        </p>
      </div>

      {/* 3 Steps */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 w-full max-w-4xl mb-10">
        {steps.map((step, index) => (
          <div 
            key={step.number}
            className="relative bg-card border border-border rounded-2xl p-6 hover:border-brand-300 dark:hover:border-brand-700 transition-all duration-300 hover:shadow-lg group"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            {/* Step number badge */}
            <div className={`absolute -top-3 left-6 w-6 h-6 rounded-full bg-gradient-to-br ${step.color} text-white text-xs font-bold flex items-center justify-center shadow-lg`}>
              {step.number}
            </div>
            
            {/* Icon */}
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${step.color} bg-opacity-10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
              <step.icon className="w-6 h-6 text-white" />
            </div>
            
            {/* Content */}
            <h3 className="font-display text-lg font-semibold text-foreground mb-2">
              {step.title}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {step.description}
            </p>
          </div>
        ))}
      </div>

      {/* CTA Buttons */}
      <div className="flex flex-col sm:flex-row items-center gap-4">
        <Button
          size="lg"
          onClick={() => navigate('/funnel/new')}
          className="bg-gradient-to-r from-brand-500 to-violet-500 hover:from-brand-600 hover:to-violet-600 text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-105"
          data-testid="create-first-funnel-btn"
        >
          <Rocket className="w-5 h-5 mr-2" />
          Crear mi primer funnel
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
        
        <Button
          variant="outline"
          size="lg"
          onClick={() => setShowDemoViewer(true)}
          className="text-muted-foreground hover:text-foreground px-6 py-6 text-base rounded-xl"
          data-testid="view-demo-btn"
        >
          <Eye className="w-5 h-5 mr-2" />
          Ver un ejemplo
        </Button>
      </div>

      {/* Demo Funnel Viewer Modal */}
      <DemoFunnelViewer 
        isOpen={showDemoViewer} 
        onClose={() => setShowDemoViewer(false)} 
      />

      {/* Trust indicators */}
      <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          <span>Sin tarjeta de crédito</span>
        </div>
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          <span>Funnel completo en ~5 min</span>
        </div>
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          <span>Editable y descargable</span>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
